// App Router
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { AppLayout } from '@/components/layout/AppLayout';

// Public pages
import { LandingPage } from '@/pages/public/LandingPage';
import { LoginPage } from '@/pages/public/LoginPage';
import { SignUpPage } from '@/pages/public/SignUpPage';

// Admin pages
import { DashboardPage } from '@/pages/admin/DashboardPage';
import { OnboardingPage } from '@/pages/admin/OnboardingPage';
import { SuperAdminPage } from '@/pages/admin/SuperAdminPage';
import { TicketsPage } from '@/pages/admin/TicketsPage';
import { TicketDetailPage } from '@/pages/admin/TicketDetailPage';
import { ArticlesPage } from '@/pages/admin/ArticlesPage';
import { FeaturesPage } from '@/pages/admin/FeaturesPage';
import { EndUsersPage } from '@/pages/admin/EndUsersPage';
import { TeamPage } from '@/pages/admin/TeamPage';
import { WidgetIntegrationPage } from '@/pages/admin/WidgetIntegrationPage';
import { GeneralSettingsPage } from '@/pages/admin/GeneralSettingsPage';
import { BrandingSettingsPage } from '@/pages/admin/BrandingSettingsPage';
import { CustomerAdminPage } from '@/pages/admin/CustomerAdminPage';
import { ProfileSettingsPage } from '@/pages/admin/ProfileSettingsPage';
import { AnalyticsDashboardPage } from '@/pages/admin/AnalyticsDashboardPage';
import { NotificationSettingsPage } from '@/pages/admin/NotificationSettingsPage';
import { ProjectsSettingsPage } from '@/pages/admin/ProjectsSettingsPage';

// Public Portal pages
import { PortalLayout } from '@/pages/portal/PortalLayout';
import { PortalHomePage } from '@/pages/portal/PortalHomePage';
import { KnowledgeBasePage } from '@/pages/portal/KnowledgeBasePage';
import { RoadmapPage } from '@/pages/portal/RoadmapPage';
import { SubmitRequestPage } from '@/pages/portal/SubmitRequestPage';
import { TicketReplyPage } from '@/pages/portal/TicketReplyPage';
import { ArticleDetailPage } from '@/pages/portal/ArticleDetailPage';
import { FeatureIdeasPage } from '@/pages/portal/FeatureIdeasPage';
import { FeatureDetailPage } from '@/pages/portal/FeatureDetailPage';

const App = () => {
  return (
    <BrowserRouter>
      <Routes>
        {/* Marketing / Landing page */}
        <Route path="/" element={<LandingPage />} />

        {/* Auth routes */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignUpPage />} />

        {/* Onboarding (protected but outside main layout) */}
        <Route
          path="/onboarding"
          element={
            <ProtectedRoute>
              <OnboardingPage />
            </ProtectedRoute>
          }
        />

        {/* Super Admin (protected, outside main layout) */}
        <Route
          path="/super-admin"
          element={
            <ProtectedRoute requireSuperAdmin>
              <SuperAdminPage />
            </ProtectedRoute>
          }
        />

        {/* Main App routes (protected with sidebar layout) */}
        <Route
          path="/app"
          element={
            <ProtectedRoute>
              <AppLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="dashboard" replace />} />
          <Route path="dashboard" element={<DashboardPage />} />

          {/* Tickets */}
          <Route path="tickets" element={<TicketsPage />} />
          <Route path="tickets/:ticketId" element={<TicketDetailPage />} />

          {/* Knowledge Base */}
          <Route path="articles" element={<ArticlesPage />} />

          {/* Feature Requests */}
          <Route path="features" element={<FeaturesPage />} />

          {/* End Users */}
          <Route path="users" element={<EndUsersPage />} />

          {/* Settings */}
          <Route path="settings" element={<GeneralSettingsPage />} />
          <Route path="settings/projects" element={<ProjectsSettingsPage />} />
          <Route path="settings/team" element={<TeamPage />} />
          <Route path="settings/branding" element={<BrandingSettingsPage />} />
          <Route path="settings/widget" element={<WidgetIntegrationPage />} />
          <Route path="settings/notifications" element={<NotificationSettingsPage />} />
          <Route path="settings/billing" element={<div>Billing (Coming Soon)</div>} />
          <Route path="settings/profile" element={<ProfileSettingsPage />} />

          {/* Analytics */}
          <Route path="analytics" element={<AnalyticsDashboardPage />} />

          {/* Super Admin - requires super admin check */}
          <Route path="super-admin/customers" element={<ProtectedRoute requireSuperAdmin><CustomerAdminPage /></ProtectedRoute>} />
        </Route>

        {/* Public Support Portal routes */}
        <Route path="/p/:orgSlug" element={<PortalLayout />}>
          <Route index element={<PortalHomePage />} />
          <Route path="kb" element={<KnowledgeBasePage />} />
          <Route path="kb/:articleSlug" element={<ArticleDetailPage />} />
          <Route path="ideas" element={<FeatureIdeasPage />} />
          <Route path="ideas/:featureId" element={<FeatureDetailPage />} />
          <Route path="roadmap" element={<RoadmapPage />} />
          <Route path="submit" element={<SubmitRequestPage />} />
          <Route path="ticket/:ticketId" element={<TicketReplyPage />} />
        </Route>

        {/* Legacy redirect from old dashboard */}
        <Route path="/dashboard" element={<Navigate to="/app/dashboard" replace />} />

        {/* Catch-all redirect */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
};

export default App;
